/**
 * Configuration loader for externalized game data
 * @module config
 */

let cachedConfig = null;

/**
 * Loads the configuration from config.json
 * @returns {Promise<Object>} The configuration object
 */
export async function loadConfig() {
  if (cachedConfig) {
    return cachedConfig;
  }

  try {
    const response = await fetch('./config.json');
    if (!response.ok) {
      throw new Error(`Failed to load config: ${response.status}`);
    }
    cachedConfig = await response.json();
    return cachedConfig;
  } catch (error) {
    console.error('Config loading error:', error);
    throw error;
  }
}

/**
 * Gets the login password from config
 * @returns {Promise<string>} The login password
 */
export async function getLoginPassword() {
  const config = await loadConfig();
  return config.credentials.login.password;
}

/**
 * Gets step data by step ID
 * @param {number} stepId - The step ID (1-4)
 * @returns {Promise<Object|null>} The step data or null if not found
 */
export async function getStepData(stepId) {
  const config = await loadConfig();
  return config.steps.find(step => step.id === stepId) || null;
}

/**
 * Gets the code for a specific step
 * @param {number} stepId - The step ID (1-4)
 * @returns {Promise<string|null>} The step code or null if not found
 */
export async function getStepCode(stepId) {
  const stepData = await getStepData(stepId);
  return stepData ? stepData.code : null;
}

/**
 * Gets all steps
 * @returns {Promise<Array>} Array of all step objects
 */
export async function getAllSteps() {
  const config = await loadConfig();
  return config.steps;
}

/**
 * Gets company information
 * @returns {Promise<Object>} Company info object
 */
export async function getCompanyInfo() {
  const config = await loadConfig();
  return config.company;
}

/**
 * Gets external resources configuration
 * @returns {Promise<Object>} External resources object
 */
export async function getExternalResources() {
  const config = await loadConfig();
  return config.externalResources;
}

/**
 * Clears the cached config (useful for testing)
 */
export function clearConfigCache() {
  cachedConfig = null;
}
